class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.refreshPromise = null;
    this.refreshTimer = null;
    this.managedChanges = 0;
    this.lastAppliedAt = 0;
    this.appliedRevision = -1;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.receiveCartUpdate(event));
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
      if (event.target.closest('[data-cart-retry]')) this.refreshContents();
      if (event.target.closest('[data-cart-clear]')) this.clearCart();
    });
    this.setHeaderCartIconAccessibility();
    document.addEventListener('aco:cart-request-start', () => {
      if (!this.managedChanges && this.classList.contains('active')) this.setRefreshState('loading');
    });
    document.addEventListener('aco:cart-request-end', () => { if (!this.managedChanges) this.scheduleRefresh(); });
    document.addEventListener('aco:kit-added', () => this.scheduleRefresh());
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.classList.contains('active')) this.scheduleRefresh();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.classList.contains('active')) this.scheduleRefresh();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    document.addEventListener('click', (event) => {
      const link = event.target.closest?.('#cart-icon-bubble');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link);
    });
    document.addEventListener('keydown', (event) => {
      const link = event.target.closest?.('#cart-icon-bubble');
      if (link && event.code === 'Space') { event.preventDefault(); this.open(link); }
    });
  }

  open(triggeredBy, refresh = true) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    const refreshed = refresh ? this.refreshContents() : Promise.resolve(true);
    this.focusDrawer();
    return refreshed;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  focusDrawer(preferredId) {
    if (!this.classList.contains('active')) return;
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    const preferred = preferredId && document.getElementById(preferredId);
    trapFocus(inner, preferred && inner.contains(preferred) ? preferred : inner);
  }

  setRefreshState(state) {
    const loading = state === 'loading';
    const failed = state === 'failed';
    this.classList.toggle('aco-cart-refreshing', loading);
    this.classList.toggle('aco-cart-refresh-failed', failed);
    this.setAttribute('aria-busy', String(loading));
    let status = this.querySelector('[data-cart-refresh-status]');
    if (!status && (loading || failed)) {
      status = document.createElement('div');
      status.className = 'aco-cart-refresh-status';
      status.dataset.cartRefreshStatus = '';
      status.setAttribute('role', 'status');
      this.querySelector('.drawer__header')?.after(status);
    }
    if (!status) return;
    status.replaceChildren();
    status.hidden = !loading && !failed;
    if (loading) status.textContent = 'Loading your cart…';
    if (failed) {
      const message = document.createElement('p');
      message.textContent = 'We couldn’t load the latest cart. Try again or open your full cart.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary';
      retry.dataset.cartRetry = '';
      retry.textContent = 'Try again';
      const link = document.createElement('a');
      link.href = window.routes?.cart_url || (window.Shopify?.routes?.root || '/') + 'cart';
      link.textContent = 'Open full cart';
      status.append(message, retry, link);
    }
  }

  scheduleRefresh() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refreshContents(), 120);
  }

  refreshContents() {
    clearTimeout(this.refreshTimer);
    if (this.refreshPromise) return this.refreshPromise;
    const startedAt = Date.now();
    this.setRefreshState('loading');
    this.refreshPromise = (async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        await acoCartRequests.waitForIdle();
        if (this.lastAppliedAt >= startedAt && this.appliedRevision === acoCartRequests.revision) return true;
        const revision = acoCartRequests.revision;
        const url = new URL(window.routes?.cart_url || (window.Shopify?.routes?.root || '/') + 'cart', location.origin);
        url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
        url.searchParams.set('_aco_cart', String(Date.now()));
        const response = await fetch(url.toString(), {method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000)});
        if (!response.ok) throw new Error('Cart refresh failed');
        const sections = await response.json();
        if (this.lastAppliedAt >= startedAt && this.appliedRevision === acoCartRequests.revision) return true;
        if (revision !== acoCartRequests.revision || acoCartRequests.pending) continue;
        this.applySections(sections);
        this.setRefreshState('ready');
        return true;
      }
      throw new Error('Cart changed while refreshing');
    })().catch(() => {
      this.setRefreshState('failed');
      this.focusDrawer();
      return false;
    }).finally(() => { this.refreshPromise = null; });
    return this.refreshPromise;
  }

  applySections(sections) {
    if (typeof sections?.['cart-drawer'] !== 'string' || typeof sections?.['cart-icon-bubble'] !== 'string') throw new Error('Incomplete cart sections');
    const parser = new DOMParser();
    const drawerDocument = parser.parseFromString(sections['cart-drawer'], 'text/html');
    const incomingDrawer = drawerDocument.querySelector('cart-drawer');
    const incomingContents = drawerDocument.querySelector('#CartDrawer');
    const incomingIcon = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
    const target = this.querySelector('#CartDrawer');
    if (!incomingDrawer || !incomingContents || !incomingContents.querySelector('.drawer__inner') || !incomingIcon || !target) throw new Error('Invalid cart sections');
    const focusId = this.contains(document.activeElement) ? document.activeElement.id : null;
    const noteWasOpen = this.querySelector('#Details-CartDrawer')?.open;
    target.innerHTML = incomingContents.innerHTML;
    this.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));
    const cartLink = document.getElementById('cart-icon-bubble');
    if (cartLink) cartLink.innerHTML = incomingIcon.innerHTML;
    if (noteWasOpen && this.querySelector('#Details-CartDrawer')) this.querySelector('#Details-CartDrawer').open = true;
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    this.setRefreshState('ready');
    this.lastAppliedAt = Date.now();
    this.appliedRevision = acoCartRequests.revision;
    clearTimeout(this.refreshTimer);
    this.focusDrawer(focusId);
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', String(summary.parentElement.hasAttribute('open')));
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', (event) => event.currentTarget.setAttribute('aria-expanded', String(!event.currentTarget.closest('details').hasAttribute('open'))));
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState?.id;
    const rendered = this.receiveCartUpdate({cartData: parsedState});
    return this.open(undefined, !rendered);
  }

  receiveCartUpdate(event) {
    if (event.cartData?.sections && !acoCartRequests.pending) {
      try { this.applySections(event.cartData.sections); return true; } catch {}
    }
    if (!this.managedChanges) this.scheduleRefresh();
    return false;
  }

  async managedChange(action) {
    if (this.managedChanges) return;
    this.managedChanges++;
    clearTimeout(this.refreshTimer);
    this.classList.add('aco-cart-changing');
    this.setAttribute('aria-busy', 'true');
    try { return await action(); }
    finally {
      this.managedChanges--;
      this.classList.remove('aco-cart-changing');
      this.setAttribute('aria-busy', 'false');
    }
  }

  clearCart() {
    return this.managedChange(async () => {
      const button = this.querySelector('[data-cart-clear]');
      if (!button || button.disabled) return;
      button.disabled = true;
      const label = button.textContent;
      button.textContent = button.dataset.loading;
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/clear.js', {
          ...fetchConfig(), body: JSON.stringify({sections:['cart-drawer','cart-icon-bubble'], sections_url:window.location.pathname})
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) throw new Error('Cart clear failed');
        if (!this.receiveCartUpdate({cartData:cart})) await this.refreshContents();
        publish(PUB_SUB_EVENTS.cartUpdate, {source:'aco-cart-clear',cartData:cart});
      } catch {
        const error = this.querySelector('#CartDrawer-CartErrors');
        if (error) error.textContent = window.cartStrings.error;
      } finally { button.disabled = false; button.textContent = label; }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [{id: 'cart-drawer', selector: '#CartDrawer'}, {id: 'cart-icon-bubble'}];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {}

  onCartUpdate() { return this.closest('cart-drawer')?.refreshContents(); }

  updateQuantity(...args) {
    const drawer = this.closest('cart-drawer');
    return drawer.managedChange(() => super.updateQuantity(...args));
  }

  getSectionsToRender() {
    return [{id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner'}, {id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section'}];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

